import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {hot} from 'react-hot-loader/root';

import Routes from './Routes';
import auth from '../utils/auth';
import {fetchProfile} from '../actions/profileActions'
import {checkForInstallPrompt} from "../utils/AppInstallHelper";
import {changeBodyColor, withMediaQueries} from "src/styles/Styles";
import {ColorBackground, ColorBackgroundXS} from "src/styles/Colors";
import {PathJoin, PathLogin} from "src/js/components/routes/NavHelper";
import LogRocket from 'logrocket';
import {APP_SHOWCASE, APP_TYPE, APP_TYPE_PROD} from "src/js/utils/services";
import {logDebug} from "src/js/utils/AppLog";
import Snackbar from "@material-ui/core/Snackbar";
import {SNACKBAR_AUTO_HIDE_DURATION} from "src/js/components/commons/ViewHelper";
import MuiAlert from "@material-ui/lab/Alert";
import {FormattedMessage} from "react-intl";
import {hideSnackbar} from "src/js/actions/uiActions";

class App extends React.Component {

  constructor(props) {
    super(props);
    this.backgroundColor = null;
    document.documentElement.style.setProperty('--recaptcha-hidden','hidden');

    this.state = {
      scale: 0,
    }
  }

  componentDidMount () {
    if (!APP_SHOWCASE && APP_TYPE === APP_TYPE_PROD) {
      LogRocket.init('kqnm2f/rab7');
    }
    if (!APP_SHOWCASE) {
      if (auth.isAuthenticated()) {
        if (APP_TYPE === APP_TYPE_PROD) {
          LogRocket.identify(auth.user.id, {
            name: auth.user.rab7Number,
          });
        }
        this.props.fetchProfile();
      }
    }

    checkForInstallPrompt();
    this.updateBgColor();
    this.adjustScale();
    window.appInFocus = true;
    window.addEventListener('resize', this.adjustScale);
    window.addEventListener('focus', this.onAppFocus);
    window.addEventListener('blur', this.onAppBlur);

    // if (APP_SHOWCASE) {
    //   this.registerCacheImageServiceWorker();
    // }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.adjustScale);
    window.removeEventListener('focus', this.onAppFocus);
    window.removeEventListener('blur', this.onAppBlur);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    this.updateBgColor();
  }

  adjustScale = () => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const isPortrait = screenWidth < screenHeight;

    const maxSize = APP_SHOWCASE ? 1280 : 2160;
    let targetWidth = 0;
    let targetHeight = 0;
    if (isPortrait) {
      targetHeight = maxSize;
      targetWidth = targetHeight/2;
    } else {
      targetWidth = maxSize;
      targetHeight = targetWidth/2;
    }

    // Calculate the scale factor based on width and height
    const scaleWidth = screenWidth / targetWidth;
    const scaleHeight = screenHeight / targetHeight;

    // Choose the smaller scaling factor to ensure the content fits in the viewport
    const newScale = Math.max(1, Math.min(scaleWidth, scaleHeight));
    const newWidth = Math.ceil(screenWidth / newScale);
    const newHeight = Math.ceil(screenHeight / newScale);
    logDebug('AppWindow', {scale: newScale, width: newWidth, height: newHeight, awidth: screenWidth, aheight: screenHeight});
    window.appWidth = newWidth;
    window.appHeight = newHeight;
    window.appScale = newScale;
    this.setState({scale: newScale});
  };

  updateBgColor = () => {
    const isXS = window.appWidth <= 600;
    let bgColor = ColorBackground;
    if (isXS) {
      bgColor = ColorBackgroundXS;
    } else {
      const path = location.pathname;
      if (path.startsWith(PathLogin)
        || path.startsWith(PathJoin)
        || path.startsWith('/recoverPassword')
        || path.startsWith('/verifyAccount')
        || path.startsWith('/redeemOffer')
        || path.startsWith('/account')) {
        bgColor = ColorBackgroundXS;
      }
    }
    if (this.backgroundColor !== bgColor) {
      this.backgroundColor = bgColor;
      changeBodyColor(this.backgroundColor);
    }
  }

  onAppFocus = () => {
    window.appInFocus = true;
    window.dispatchEvent(new Event('onAppDidFocus'));
  };

  onAppBlur = () => {
    window.appInFocus = false;
    window.dispatchEvent(new Event('onAppDidBlur'));
  };

  render() {
    const {app} = this.props;
    const {scale} = this.state;
    if (!scale) {
      return null;
    }

    let exStyle = null;
    if (scale > 1) {
      exStyle = {
        transform: `scale(${scale})`,
        transformOrigin: app.isLTR ? 'top left' : 'top right',
        width: window.appWidth,
        height: window.appHeight,
      }
    }
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          ...exStyle
        }}>
        {/*<PushNotification />*/}
        <div
          className='app-container'
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
          }}><Routes/></div>
        {this.renderSnackBar()}
      </div>
    );
  }

  renderSnackBar() {
    const {snackbar} = this.props;
    if (!snackbar.show) {
      return null;
    }
    return (
      <Snackbar open autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION} onClose={this.props.hideSnackbar}>
        <MuiAlert severity={snackbar.type || "success"} onClose={this.props.hideSnackbar} elevation={6} variant="filled">
          {snackbar.message ? snackbar.message : (snackbar.messageId ? <FormattedMessage id={snackbar.messageId} /> : null)}
        </MuiAlert>
      </Snackbar>
    );
  }

  registerCacheImageServiceWorker() {
    if ("serviceWorker" in navigator && window.indexedDB) {
      navigator.serviceWorker
        .register("/cache-image-sw.js")
        .then((registration) => {
          console.log("Cache Image SW registered with scope:", registration.scope);
        })
        .catch((error) => {
          console.error("Cache Image SW registration failed:", error);
        });
    } else {
      console.warn("Service Workers or indexedDB are not supported in this browser.");
    }
  }
}

App.propTypes = {
  children: PropTypes.element,
  fetchProfile: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    app: state.app,
    snackbar: state.ui.snackbar,
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    fetchProfile: () => dispatch(fetchProfile()),
    hideSnackbar: () => dispatch(hideSnackbar()),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withMediaQueries([
    ['isXS', theme => theme.breakpoints.down('xs'), {defaultMatches: true}],
  ])(hot(App))
);
